<script setup>
import Vapor from "laravel-vapor";
import BrandButton from "@/Components/Brand/BrandButton.vue";

function asset(path) {
    return Vapor.asset(path);
}

// Exact puzzle-piece outline from Figma (node 80:301); recoloured via currentColor.
const puzzlePath =
    "M6.61875 0.199805C7.14889 0.199849 7.62286 0.367271 8.03281 0.699805C8.40467 1.00154 8.67061 1.39471 8.83555 1.8707H12.0084C12.2963 1.87082 12.5448 1.97924 12.7418 2.19004C12.9392 2.4003 13.0367 2.66054 13.0367 2.95664V6.36191C13.4876 6.54066 13.8565 6.82774 14.1373 7.22324C14.4468 7.65895 14.6002 8.15933 14.6002 8.71641C14.6002 9.27346 14.4468 9.77388 14.1373 10.2096C13.8562 10.6052 13.4869 10.8906 13.0367 11.0689V14.4762C13.0367 14.7351 12.9628 14.9662 12.8121 15.1607L12.7428 15.2418C12.5456 15.4531 12.2967 15.562 12.0084 15.5621H8.57773L8.53672 15.4156C8.39782 14.9169 8.1506 14.5242 7.79746 14.2291C7.44429 13.934 7.05327 13.7897 6.61875 13.7896C6.18415 13.7896 5.79327 13.934 5.44004 14.2291C5.08674 14.5243 4.83873 14.9167 4.6998 15.4156L4.65977 15.5621H1.2291C0.940524 15.5621 0.691034 15.4534 0.49375 15.2418V15.2408C0.296909 15.0312 0.19984 14.772 0.199805 14.4762V10.8258L0.34043 10.7818C0.77137 10.647 1.13146 10.3921 1.42441 10.0123C1.71315 9.63799 1.85797 9.20884 1.85801 8.71641C1.85801 8.22386 1.71322 7.79391 1.42441 7.41953C1.13152 7.03998 0.771207 6.78577 0.34043 6.65098L0.199805 6.60703V2.95664C0.199805 2.66054 0.297309 2.4003 0.494727 2.19004C0.691901 1.97895 0.940862 1.87071 1.2291 1.8707H4.40098C4.56628 1.39422 4.8322 1.00113 5.20371 0.699805C5.61379 0.36706 6.08839 0.199805 6.61875 0.199805ZM6.61875 1.48691C6.3668 1.48691 6.12671 1.57257 5.89414 1.75547C5.68293 1.92157 5.53838 2.18243 5.4791 2.56699V3.15684H1.4293V5.74277C1.92575 6.02076 2.32289 6.41678 2.6168 6.93027C2.93078 7.47806 3.0875 8.07493 3.0875 8.71641C3.08747 9.354 2.93015 9.94903 2.61582 10.4977C2.32243 11.0106 1.92541 11.4071 1.4293 11.6881V14.276H3.83359C4.08494 13.7131 4.45674 13.2764 4.95469 12.9771C5.4772 12.6629 6.0333 12.5035 6.61875 12.5035C7.20412 12.5036 7.75985 12.663 8.28281 12.9771C8.78094 13.2764 9.15299 13.7129 9.40391 14.276H11.8082V9.94785H12.3717C12.7183 9.88509 12.958 9.73571 13.1139 9.50938V9.5084C13.2871 9.25802 13.3707 8.99545 13.3707 8.71641C13.3707 8.43711 13.2877 8.17392 13.1148 7.92344C12.9583 7.69676 12.7177 7.54705 12.3697 7.48496H11.8082V3.15684H7.7584V2.56602C7.69855 2.18304 7.55441 1.92213 7.34336 1.75547C7.11085 1.57262 6.87062 1.48696 6.61875 1.48691Z";
</script>

<template>
    <div>
        <!-- Desktop: tall card (collage + headline) over a navy CTA -->
        <div class="overflow-hidden rounded-brand bg-white p-5 max-lg:hidden">
            <img
                :src="
                    asset('images/dashboard/cta-browser-extension-content.png')
                "
                alt="Quickly save gifts anywhere you shop"
                class="w-full"
            />

            <!-- Navy CTA, red extension icon at rest; wipes to red + white on hover -->
            <BrandButton
                :href="route('browser-extensions')"
                icon-rest-class="text-brand-red"
                class="mt-3 h-[48px] w-full px-4 text-[15px]"
            >
                <template #icon>
                    <!-- Even height keeps the centering offset integral in the
                         48px button; an odd height rasterizes the resting and
                         wipe copies a half-pixel apart (same fix as
                         FollowButton). -->
                    <svg
                        viewBox="0 0 14.8 15.7621"
                        class="h-[18px] w-[17px]"
                        fill="currentColor"
                        stroke="currentColor"
                        stroke-width="0.4"
                        aria-hidden="true"
                    >
                        <path :d="puzzlePath" />
                    </svg>
                </template>
                Add Browser Extension
            </BrandButton>
        </div>

        <!-- Mobile: horizontal "Gifts on the Go" card (Figma 224-363) -->
        <div
            class="relative flex min-h-[136px] items-center gap-[22px] overflow-hidden rounded-brand bg-[#bfb1ad] py-3 pl-[13px] pr-[18px] lg:hidden"
        >
            <!-- Lighter accent strip behind the cooler -->
            <div class="absolute inset-y-0 left-0 w-[61px] bg-[#e2dad8]"></div>

            <!--
                Cooler spilling over a white disc, with the red add badge on its
                front wheel. The disc no longer clips the cooler (BH-35): in the
                mock the 112px photo overhangs the 97px disc on the left and
                bottom, and the badge sits just inside the disc's lower edge.
            -->
            <div class="relative size-[97px] shrink-0 rounded-full bg-white">
                <img
                    :src="asset('images/dashboard/cta-cooler.png')"
                    alt="Save gifts while you browse"
                    class="absolute -left-2 top-1 w-[112px] max-w-none"
                />
                <span
                    aria-hidden="true"
                    class="absolute left-[35px] top-[83px] grid size-[26px] place-items-center rounded-full bg-brand-red text-white"
                >
                    <svg
                        viewBox="0 0 14 14"
                        class="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.6"
                        stroke-linecap="round"
                    >
                        <path d="M7 2.5v9M2.5 7h9" />
                    </svg>
                </span>
            </div>

            <!-- Copy + CTA -->
            <div class="relative flex min-w-0 flex-1 flex-col gap-1">
                <p
                    class="font-brand-display text-[13px] font-bold uppercase italic leading-[0.9] tracking-[-0.01em] text-[#554b49]"
                >
                    Add Gifts While You Browse
                </p>
                <p
                    class="font-brand-display text-[27px] font-black uppercase italic leading-[0.9] tracking-[-0.01em] text-white"
                >
                    Gifts on the Go
                </p>

                <BrandButton
                    :href="route('browser-extensions')"
                    icon-rest-class="text-brand-red"
                    class="mt-2 h-[38px] w-full px-3 text-[13px]"
                >
                    <template #icon>
                        <svg
                            viewBox="0 0 14.8 15.7621"
                            class="h-[16px] w-[15px]"
                            fill="currentColor"
                            stroke="currentColor"
                            stroke-width="0.4"
                            aria-hidden="true"
                        >
                            <path :d="puzzlePath" />
                        </svg>
                    </template>
                    Add Browser Extension
                </BrandButton>
            </div>
        </div>
    </div>
</template>
